import type { ApiErrorPayload } from '@/types/api';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000/api/v1';
const TOKEN_KEY = 'manejai_token';

export class ApiError extends Error {
  code?: string;
  status: number;
  fieldErrors?: Record<string, string[]>;

  constructor(
    message: string,
    status: number,
    code?: string,
    fieldErrors?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
  }
}

type LaravelErrorBody = {
  message?: string;
  errors?: Record<string, string[]>;
};

function extractErrorMessage(
  body: ApiEnvelope<unknown> | LaravelErrorBody | null,
  status: number,
  statusText: string,
): string {
  if (!body) {
    return statusText || `Erro HTTP ${status}.`;
  }

  if ('errors' in body && body.errors) {
    const firstField = Object.values(body.errors)[0]?.[0];
    if (firstField) return firstField;
  }

  if ('message' in body && typeof body.message === 'string' && body.message.trim()) {
    return body.message;
  }

  const envelope = body as ApiEnvelope<unknown>;
  if (envelope.error?.message) {
    return envelope.error.message;
  }

  return statusText || `Erro HTTP ${status}.`;
}

function parseErrorBody(
  body: ApiEnvelope<unknown> | LaravelErrorBody | null,
  status: number,
  statusText: string,
): ApiError {
  const fieldErrors = body && 'errors' in body ? body.errors : undefined;
  const envelope = body as ApiEnvelope<unknown> | null;

  return new ApiError(
    extractErrorMessage(body, status, statusText),
    status,
    fieldErrors ? 'validation_failed' : envelope?.error?.code,
    fieldErrors,
  );
}

/** Mensagem legível para qualquer erro capturado em formulários/mutações. */
export function getErrorMessage(err: unknown, fallback = 'Ocorreu um erro.'): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error && err.message) return err.message;
  if (typeof err === 'string' && err.trim()) return err;
  return fallback;
}

/** Todas as mensagens de validação (ex.: importação CSV com várias linhas). */
export function getAllErrorMessages(err: unknown, fallback = 'Ocorreu um erro.'): string[] {
  if (err instanceof ApiError && err.fieldErrors) {
    const messages = Object.values(err.fieldErrors).flat().filter(Boolean);
    if (messages.length > 0) return messages;
  }
  return [getErrorMessage(err, fallback)];
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

type ApiEnvelope<T> = {
  success: boolean;
  data?: T;
  error?: ApiErrorPayload;
};

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getToken();
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(options.body && !isFormData ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
    });
  } catch {
    throw new ApiError('Falha de conexão com o servidor.', 0, 'network_error');
  }

  let body: ApiEnvelope<T> | LaravelErrorBody | null = null;

  try {
    body = (await response.json()) as ApiEnvelope<T> | LaravelErrorBody;
  } catch {
    if (!response.ok) {
      throw new ApiError(response.statusText || `Erro HTTP ${response.status}.`, response.status);
    }
    throw new ApiError('Resposta inválida do servidor.', response.status);
  }

  if (!response.ok) {
    throw parseErrorBody(body, response.status, response.statusText);
  }

  const envelope = body as ApiEnvelope<T>;
  if (envelope?.success === false) {
    throw parseErrorBody(body, response.status, response.statusText);
  }

  return envelope.data as T;
}

export async function apiUpload<T>(path: string, formData: FormData, method = 'POST'): Promise<T> {
  return apiFetch<T>(path, { method, body: formData });
}

export { API_BASE };
